import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { Radar, AlertCircle, AlertTriangle } from 'lucide-react';
import { loginSchema, flattenErrors, type FieldErrors } from '@/lib/validation';
import { FieldError } from '@/components/ui/FieldError';

export function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [oidcEnabled, setOidcEnabled] = useState(false);

  useEffect(() => {
    api.getOIDCConfig().then(cfg => {
      if (cfg.enabled) setOidcEnabled(true);
    }).catch(() => {});
  }, []);

  // If already logged in, redirect
  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setWarning('');
    setFieldErrors({});

    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      setFieldErrors(flattenErrors(result));
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err: any) {
      if (err.status === 429) {
        const retryMin = err.retry_after ? Math.ceil(err.retry_after / 60) : 15;
        setError(`Account temporarily locked. Try again in ${retryMin} minute${retryMin !== 1 ? 's' : ''}.`);
        setWarning('');
      } else if (typeof err.remaining_attempts === 'number' && err.remaining_attempts <= 2) {
        setError(err.message || 'Login failed');
        if (err.remaining_attempts === 0) {
          setWarning('');
          setError('Account temporarily locked due to too many failed attempts.');
        } else {
          setWarning(`${err.remaining_attempts} attempt${err.remaining_attempts !== 1 ? 's' : ''} remaining before your account is temporarily locked.`);
        }
      } else {
        setError(err.message || 'Login failed');
        setWarning('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sabio-purple to-indigo-600 flex items-center justify-center shadow-lg shadow-sabio-purple/20 mb-4">
            <Radar className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sabio-purple-light to-sabio-yellow bg-clip-text text-transparent">
            Sentinel
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Event Intelligence Platform</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {warning && !error.includes('locked') && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {warning}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setFieldErrors(fe => ({ ...fe, email: '' })); }}
              className={`w-full px-3 py-2 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${fieldErrors.email ? 'border-destructive' : 'border-border'}`}
              placeholder="you@example.com"
              autoComplete="email"
            />
            <FieldError error={fieldErrors.email} />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setFieldErrors(fe => ({ ...fe, password: '' })); }}
              className={`w-full px-3 py-2 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${fieldErrors.password ? 'border-destructive' : 'border-border'}`}
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <FieldError error={fieldErrors.password} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {oidcEnabled && (
          <>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <a
              href="/api/auth/oidc/authorize"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border border-border bg-background text-foreground font-medium text-sm hover:bg-muted transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Sign in with Microsoft
            </a>
          </>
        )}

        <p className="text-center text-xs text-muted-foreground mt-6">
          Powered by <span className="font-semibold text-sabio-yellow">Sabio</span>
        </p>
      </div>
    </div>
  );
}
